import { useState } from "react";
import { useAuth } from "../App";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Lightning, Copy, CheckCircle, ClipboardText, ArrowSquareOut, Buildings, ListChecks, FileText } from "@phosphor-icons/react";
import { toast } from "sonner";

const CopyBlock = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  if (!value) return null;
  return (
    <div className="bg-zinc-900 border border-zinc-800 rounded-sm p-3">
      <div className="flex items-center justify-between mb-1">
        <span className="text-[10px] uppercase tracking-wider text-zinc-500 font-semibold">{label}</span>
        <button onClick={handleCopy} className="text-xs text-emerald-400 hover:text-emerald-300 flex items-center gap-1" data-testid={`copy-${label.toLowerCase().replace(/\s/g, '-')}`}>
          {copied ? <><CheckCircle className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
        </button>
      </div>
      <p className="text-sm text-zinc-300 whitespace-pre-wrap">{value}</p>
    </div>
  );
};

export const PrepKitModal = ({ grant, open, onOpenChange }) => {
  const { user } = useAuth();
  const profile = user?.nonprofit_profile;

  const profileSummary = profile?.org_name ? [
    profile.org_name && `Organization: ${profile.org_name}`,
    profile.ein && `EIN: ${profile.ein}`,
    profile.mission && `Mission: ${profile.mission}`,
    profile.county && `Location: ${profile.county}, SC`,
    profile.annual_budget && `Annual Budget: ${profile.annual_budget}`,
    profile.staff_count && `Staff: ${profile.staff_count}`,
    profile.focus_areas?.length && `Focus Areas: ${profile.focus_areas.join(", ")}`,
  ].filter(Boolean).join("\n") : "";

  const requirements = Array.isArray(grant.requirements) ? grant.requirements : [];
  const tips = Array.isArray(grant.success_tips) ? grant.success_tips : [];

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-zinc-950 border-zinc-800 max-w-lg max-h-[85vh] overflow-y-auto" data-testid="prep-kit-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-lg">
            <ClipboardText className="w-5 h-5 text-emerald-400" />
            Application Prep Kit
          </DialogTitle>
        </DialogHeader>

        <p className="text-sm text-zinc-400 -mt-2">
          Everything you need before applying. Copy what you need, then click through to the funder's site.
        </p>

        <div className="space-y-3 mt-2">
          {/* Grant Summary */}
          <div className="bg-emerald-500/5 border border-emerald-500/20 rounded-sm p-3">
            <div className="flex items-center gap-2 mb-2">
              <Buildings className="w-4 h-4 text-emerald-400" />
              <span className="text-sm font-semibold">{grant.title}</span>
            </div>
            <div className="text-xs text-zinc-400 space-y-0.5">
              {grant.funder_name && <p>Funder: {grant.funder_name}</p>}
              {(grant.amount_min > 0 || grant.amount_max > 0) && (
                <p>Amount: ${grant.amount_min?.toLocaleString()} – ${grant.amount_max?.toLocaleString()}</p>
              )}
              {grant.deadline && <p>Deadline: {new Date(grant.deadline + "T00:00:00").toLocaleDateString()}</p>}
            </div>
          </div>

          {/* Requirements Checklist */}
          {requirements.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <ListChecks className="w-4 h-4 text-zinc-400" />
                <span className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Requirements Checklist</span>
              </div>
              <div className="space-y-1.5">
                {requirements.map((req, i) => (
                  <label key={i} className="flex items-start gap-2 text-sm text-zinc-300 cursor-pointer">
                    <input type="checkbox" className="mt-0.5 accent-emerald-500 rounded" data-testid={`req-check-${i}`} />
                    <span>{req}</span>
                  </label>
                ))}
              </div>
            </div>
          )}

          {/* Your Nonprofit Profile - Ready to Paste */}
          {profileSummary && (
            <CopyBlock label="Your Nonprofit Profile" value={profileSummary} />
          )}

          {/* Mission Statement */}
          {profile?.mission && (
            <CopyBlock label="Your Mission Statement" value={profile.mission} />
          )}

          {/* Success Tips */}
          {tips.length > 0 && (
            <div>
              <div className="flex items-center gap-1.5 mb-2">
                <FileText className="w-4 h-4 text-zinc-400" />
                <span className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">Tips for This Grant</span>
              </div>
              <ul className="space-y-1">
                {tips.map((tip, i) => (
                  <li key={i} className="text-sm text-zinc-400 flex items-start gap-2">
                    <span className="text-emerald-500 mt-0.5">&#10003;</span>
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Go to Funder's Site */}
          {grant.funder_website && (
            <a
              href={grant.funder_website}
              target="_blank"
              rel="noopener noreferrer"
              className="w-full inline-flex items-center justify-center gap-2 rounded-sm px-4 py-3 text-sm font-bold bg-emerald-600 hover:bg-emerald-500 text-white transition-colors"
              data-testid="prep-kit-apply-btn"
            >
              <ArrowSquareOut className="w-5 h-5" />
              Go to Funder's Application Site
            </a>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};
